<template>
  <div class="container">
    <div class="notification is-danger" v-show="user.account.delinquent">
      <h3 class="title is-3">Action needed!</h3>
      <p>
        <span v-if="user._id !== user.account.owner">
          It seems we were unable to process your payment for your subscription.
          Please choose a plan from below to continue using our platform.
        </span>
        <span v-else>
          It seems we were unable to process your payment for your subscription.
          Please contact the account owner to update the payment method
          associated with the subscription in order to continue using our
          platform.
        </span>
      </p>
    </div>
    <div class="notification is-warning" v-show="isLifetime">
      <h3 class="title is-3">Attention!</h3>
      <p>
        <span>
          You are currently on the
          <strong>{{ user.account.subscription.plan.name }}</strong> plan. If
          you decide to change plans now, you <strong>WILL</strong> permanently
          lose available benefits related to this special offer.
        </span>
      </p>
      <br>
      <p>
        <span>
          Your existing data such as documents, links and visits will remain unchanged.
        </span>
      </p>
    </div>
    <div class="interval-selector-container mb-5">
      <h2 class="title is-2 has-text-centered">
        Need more features in your subscription?
      </h2>
      <h4 class="subtitle is-4 has-text-grey has-text-centered">
        Select the plan that better accomodates to your needs.
      </h4>
      <div class="has-text-centered">
        <div class="control">
          <div class="field">
            <label for="sw-watermark">Monthly</label>
            &nbsp;
            <input
              id="sw-watermark"
              type="checkbox"
              name="watermark"
              class="switch is-rounded is-outlined"
              v-model="yearlyInterval"
              :checked="yearlyInterval"
            />
            <label for="sw-watermark">
              Yearly
              <!-- <strong>(up to {{ maxPercentDiscount }}% OFF)</strong> -->
              <strong>(up to 40% OFF)</strong>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="pricing-table">
      <div
        class="pricing-plan"
        :class="{ 'is-active': plan._id === currentPlanId }"
        v-for="plan in availablePlans"
        :key="plan.id"
      >
        <div class="plan-header">
          <span class="is-block">{{ plan.name }}</span>
          <button
            @click="confirmPlanChange(plan)"
            class="button is-fullwidth is-primary"
            :disabled="user._id !== user.account.owner"
            v-if="plan._id !== currentPlanId"
          >
            <span v-if="plan.name === 'Free'">Downgrade</span>
            <strong v-else>Get {{ plan.name }} now</strong>
          </button>
          <small class="is-block has-text-primary" v-else>My plan</small>
        </div>
        <div class="plan-price">
          <span class="plan-price-amount">
            <span class="plan-price-currency">$</span>
            {{ plan | getDiscountedPrice }}
            <span class="is-uppercase">{{ plan.stripe.currency }}</span>
          </span>
          /month
        </div>
        <div class="plan-price" v-if="yearlyInterval && plan.stripe.amount !== 0">
          <span class="plan-price-amount is-size-6">
              ${{ plan | totalPrice }}
            <span class="is-uppercase has-text-grey is-size-6">{{ plan.stripe.currency }}</span>
          </span>
          /{{ plan.stripe.interval || plan.stripe.recurring.interval }}
        </div>
        <div class="plan-items">
          <div
            class="plan-item"
            v-for="(feature, index) in filterFeatures(plan.features)"
            :key="index"
          >
            <span class="icon has-text-danger" v-if="feature.value === false">
              <i class="fas fa-times"></i>
            </span>
            <span class="icon has-text-primary" v-else>
              <i class="fas fa-check"></i>
            </span>
            &nbsp;
            <span>
              <span v-if="!isBoolean(feature.value)">
                {{ convertValue(feature) }}
                &nbsp;
              </span>
              {{ feature.name }}
            </span>
          </div>
        </div>
        <div class="plan-footer">
          <button
            @click="confirmPlanChange(plan)"
            class="button is-fullwidth is-primary"
            :disabled="user._id !== user.account.owner"
            v-if="plan._id !== currentPlanId"
          >
            <span v-if="plan.name === 'Free'">Downgrade</span>
            <strong v-else>Get {{ plan.name }} now</strong>
          </button>
          <strong class="has-text-primary" v-else>My plan</strong>
        </div>
      </div>
    </div>
    <confirmation :modal="showConfirm">
      <header class="modal-card-head">
        <p class="modal-card-title">Confirmation needed</p>
        <button
          class="delete"
          aria-label="close"
          @click="showConfirm = false"
        />
      </header>
      <section class="modal-card-body">
        <h4 class="title is-4 has-text-danger mb-3">
          Are you sure you wish to change plans?
        </h4>
        <p class="is-size-5 mb-1">
          Changing plans may lead to permanent loss of your current benefits,
          including:
        </p>
        <div v-for="feature in featuresToLose" :key="feature.name">
          <p class="is-size-6">- {{ feature.name }}</p>
          <p
            v-if="feature.name === 'Document Password Protection'"
            class="help is-danger ml-4 mb-0 mt-0"
          >
            *All your password protected documents will lose their protection.
          </p>
          <p
            v-if="feature.name === 'Authorized Users'"
            class="help is-danger ml-4 mb-0 mt-0"
          >
            *All your documents will lose their authorized users.
          </p>
        </div>
      </section>
      <footer class="modal-card-foot">
        <button class="button is-primary" @click="updatePlan">
          Confirm
        </button>
        <button @click="showConfirm = false" class="button">
          Close
        </button>
      </footer>
    </confirmation>
    <!-- Payment form dialog -->
    <payment-form-dialog
      :open="togglePaymentForm"
      @close="togglePaymentForm = !togglePaymentForm"
      @updated="updatePlan"
    />
  </div>
</template>
<script>
import numeral from '@/utils/numeral';

export default {
  components: {
    Confirmation: () => import('@/components/Confirmation.vue'),
    PaymentFormDialog: () => import('@/components/shared/PaymentFormDialog.vue')
  },
  data() {
    return {
      plans: [],
      currentPlanId: null,
      desiredPlan: null,
      showConfirm: false,
      yearlyInterval: true,
      togglePaymentForm: false
    };
  },
  computed: {
    price() {
      return amount => amount / 100;
    },
    isLifetime() {
      return this.user.account.subscription.plan.id.indexOf('lifetime') !== -1;
    },
    availablePlans() {
      return this.plans.filter(p => {
        if (p.stripe.amount === 0) {
          return true;
        }
        if (this.yearlyInterval) {
          if (
            p.stripe.interval === 'year' ||
            (p.stripe.recurring && p.stripe.recurring?.interval === 'year')
          ) {
            return true;
          }
        } else {
          if (
            p.stripe.interval === 'month' ||
            (p.stripe.recurring && p.stripe.recurring?.interval === 'month')
          ) {
            return true;
          }
        }
        return false;
      });
    },
    maxPercentDiscount() {
      const discounts = this.plans.map(p => p.discount || 0);
      const max = Math.max(...discounts);
      const percent = max * 100;
      return Math.ceil(percent) || 0;
    },
    currentPlan() {
      if (!this.plans.length) {
        return null;
      }
      return this.plans.find(plan => plan._id === this.currentPlanId);
    },
    featuresToLose() {
      if (!this.currentPlan || !this.desiredPlan) {
        return [];
      }
      const featuresToLose = [];
      const currentFeatures = this.currentPlan.features;
      const desiredFeatures = this.desiredPlan.features;
      for (const key in currentFeatures) {
        if (currentFeatures[key].value && !desiredFeatures[key].value) {
          featuresToLose.push(currentFeatures[key]);
        }
      }
      return featuresToLose;
    }
  },
  filters: {
    totalPrice: function(plan) {
      return (plan.stripe.unit_amount || plan.stripe.amount) / 100;
    },
    getDiscountedPrice: function(plan) {
      let amount = parseInt(plan.stripe.unit_amount || plan.stripe.amount, 10);
      /* Old code for discounted price
      const inversePercent = 1 - plan.discount;
      return Math.ceil((amount / inversePercent) / 100, 0);
      */
      const isYearly =
        plan.stripe.interval === 'yearly' ||
        (plan.stripe.recurring && plan.stripe.recurring.interval === 'year');
      amount = amount && amount / 100;
      if (isYearly) {
        amount = Math.ceil(amount / 12, 0);
      }
      return amount;
    }
  },
  methods: {
    /**
     * Checks if the value is a boolean.
     */
    isBoolean(value) {
      return typeof value === `boolean`;
    },
    /**
     * Converts the object hash into an Array and filters the content.
     */
    filterFeatures(features) {
      const array = Object.keys(features).map(key => features[key]);
      const filtered = array.filter(feature => feature.show !== false);
      return filtered;
    },
    /**
     * Converts numeric values to Bytes format.
     */
    convertValue(feature) {
      let value = feature.value;
      if (feature.convert) {
        value = numeral.convert(value);
      }
      return value;
    },
    /**
     * Shows a confirmation dialog before performing the update.
     */
    confirmPlanChange(plan) {
      if(!this.featuresToLose.length && plan.name === 'Free'){
        this.downgradeToFree();
        return;
      }
      this.desiredPlan = plan;
      if (!this.featuresToLose.length) {
        this.updatePlan(plan);
        return;
      }
      this.showConfirm = true;
    },
    downgradeToFree() {
      this.$axios
        .patch(
          `${this.$API.subscriptions}/${this.user.account.subscription._id}/plan`,
          { plan: 'free' }
        )
        .then(async response => {
          if (response.data.success) {
            this.$notify({
              group: 'alert',
              title: 'Success!',
              text: 'Successfully updated your subscription.',
              type: 'success'
            });
            this.user = (await this.$axios.get(this.$API.me)).data;
            this.$store.commit('auth/setUser', this.user);
            if (this.$route.query && this.$route.query.from) {
              this.$router.push({ path: this.$route.query.from });
            } else {
              this.$router.push({ path: '/dashboard' });
            }
          }
        })
        .catch(error => {
          console.log('Error:', error);
          this.$notify({
            group: 'alert',
            title: 'FAILED!',
            text:
              'Something went wrong while processing your request. Please try again later.',
            type: 'error'
          });
        });
    },
    /**
     * Updates the selected plan in the database.
     */
    updatePlan() {
      this.updatePlan_v2();
    },
    updatePlan_v1(plan) {
      this.togglePaymentForm = false;
      this.showConfirm = false;
      this.desiredPlan = this.desiredPlan || plan;
      if (!this.desiredPlan) {
        return this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: 'Missing desired plan!',
          type: 'error'
        });
      }
      this.$axios
        .patch(
          `${this.$API.subscriptions}/${this.user.account.subscription._id}/plan`,
          this.desiredPlan
        )
        .then(async response => {
          if (response.data.success) {
            this.$notify({
              group: 'alert',
              title: 'Success!',
              text: 'Successfully updated your subscription.',
              type: 'success'
            });
            this.user = (await this.$axios.get(this.$API.me)).data;
            this.$store.commit('auth/setUser', this.user);
            if (this.$route.query && this.$route.query.from) {
              this.$router.push({ path: this.$route.query.from });
            } else {
              this.$router.push({ path: '/dashboard' });
            }
          }
        })
        .catch(error => {
          console.log('Error: ', error);
          this.$notify({
            group: 'alert',
            title: 'FAILED!',
            text:
              error.response && error.response.data
                ? error.response.data.message
                : 'Something went wrong while processing your request. Please try again later.',
            type: 'error'
          });
          // Activate the payment modal which will get the user's details and
          // execute the update plan functiona automatically.
          this.togglePaymentForm = true;
        });
    },
    updatePlan_v2() {
      this.$router.push({
        path: '/profile/checkout',
        query: { price: this.desiredPlan?.stripe?.id }
      });
    }
  },
  created() {
    this.user = this.$store.getters['auth/getUser'];

    // Fetch the available plans
    this.$axios
      .get(this.$API.plans)
      .then(response => {
        this.plans = response.data;
      })
      .catch(error => {
        console.error(error);
      });

    // Fetch the user's current plan
    this.$axios
      .get(`${this.$API.accounts}/${this.user.account._id}/plan`)
      .then(response => {
        this.currentPlanId = response.data;
      })
      .catch(error => {
        console.error(error);
      });
  }
};
</script>
<style lang="scss" scoped>
.plans-container {
  display: flex;
  flex-grow: 1;
  min-height: calc(100% - 148px);

  .card {
    height: 100%;

    .card-content {
      min-height: calc(100% - 97px);
    }
  }
}

.pricing-table {
  .pricing-plan.is-active {
    .plan-footer {
      .button {
        background-color: white;
        border-color: #dbdbdb;
        border-width: 1px;
        color: #363636;
      }
    }
  }
}

.notification[style*='none'] + .plans-container {
  min-height: 100%;
}
</style>
